import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { connectivity } from '../lib/connectivity';
import { renumberSequence } from '../lib/fieldPhotos';
import { drain, getItemsByJob, retryFailedItems, type QueueItem } from '../lib/syncQueue';

type Phase = 'renumbering' | 'uploading' | 'verifying' | 'finalizing' | 'done' | 'failed';

export function SyncProgress() {
  const { jobId } = useParams<{ jobId: string }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const choice = (search.get('choice') as 'renumber' | 'keep_gaps' | null) ?? null;

  const [phase, setPhase] = useState<Phase>('uploading');
  const [items, setItems] = useState<QueueItem[]>([]);
  const [online, setOnline] = useState(connectivity.isOnline());
  const [err, setErr] = useState<string | null>(null);
  const startedRef = useRef(false);

  // Watch connectivity for the offline indicator
  useEffect(() => connectivity.onStatusChange(setOnline), []);

  // Poll the queue every second to reflect drain progress in the UI
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const list = await getItemsByJob(jobId);
        if (!cancelled) setItems(list);
      } catch {
        // ignore transient IDB errors
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [jobId]);

  // Kick the whole flow once
  useEffect(() => {
    if (!jobId || startedRef.current) return;
    startedRef.current = true;
    (async () => {
      try {
        if (choice) {
          setPhase('renumbering');
          await renumberSequence(
            jobId,
            choice === 'renumber' ? 'renumber' : 'gap_preserved',
          );
        }
        setPhase('uploading');
        void drain();
      } catch (e) {
        setErr((e as Error).message);
        setPhase('failed');
      }
    })();
  }, [jobId, choice]);

  // Transition to finalizing / done once the queue is fully drained
  useEffect(() => {
    if (phase === 'renumbering' || phase === 'done' || phase === 'failed') return;
    if (items.length === 0) return;
    const failed = items.some((i) => i.status === 'failed');
    const allConfirmed = items.every((i) => i.status === 'confirmed');
    if (failed) setPhase('failed');
    else if (allConfirmed) setPhase('finalizing');
  }, [items, phase]);

  // Once finalizing kicks in, head to completion
  useEffect(() => {
    if (phase !== 'finalizing' || !jobId) return;
    const t = window.setTimeout(() => {
      navigate(`/job/${jobId}/complete?choice=${choice ?? ''}`);
    }, 400);
    return () => window.clearTimeout(t);
  }, [phase, jobId, choice, navigate]);

  const total = items.length;
  const confirmed = items.filter((i) => i.status === 'confirmed').length;
  const pending = items.filter(
    (i) => i.status === 'pending' || i.status === 'uploading',
  ).length;
  const failedCount = items.filter((i) => i.status === 'failed').length;
  const allConfirmed = total > 0 && confirmed === total;
  const pct = total ? Math.round((confirmed / total) * 100) : 0;

  const currentUploading = items.find((i) => i.status === 'uploading');

  const phaseLabel = allConfirmed
    ? 'Success'
    : phase === 'renumbering'
      ? 'Renumbering'
      : phase === 'finalizing'
      ? 'Pushing to EVA'
      : phase === 'failed'
      ? 'Sync failed'
      : 'Uploading';

  const R = 52;
  const C = 2 * Math.PI * R;

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-black p-4 text-white">
      <div className="relative">
        <svg width="140" height="140" viewBox="0 0 140 140">
          <circle cx="70" cy="70" r={R} stroke="white" strokeOpacity="0.15" strokeWidth="8" fill="none" />
          <circle
            cx="70"
            cy="70"
            r={R}
            stroke="#0d9488"
            strokeWidth="8"
            fill="none"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - pct / 100)}
            strokeLinecap="round"
            transform="rotate(-90 70 70)"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-bold">{pct}%</div>
          <div className="text-xs opacity-70">
            {confirmed} / {total}
          </div>
        </div>
      </div>

      <div className="mt-6 text-sm font-semibold uppercase tracking-wide">
        {phaseLabel}
      </div>
      {currentUploading && (
        <div className="mt-1 text-xs opacity-70">
          photo #{currentUploading.sequence_index}
        </div>
      )}

      <div className="mt-4 flex items-center gap-2 text-xs">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            online ? 'bg-eva-teal' : 'bg-bracket-amber'
          }`}
        />
        <span className="opacity-80">
          {allConfirmed ? 'sync complete' : online ? 'syncing live' : 'queued offline'}
        </span>
      </div>

      {!online && pending > 0 && (
        <div className="mt-3 max-w-xs rounded-lg bg-white/10 p-3 text-center text-xs">
          No signal — photos queued. Sync will resume automatically.
        </div>
      )}

      {err && (
        <div className="mt-3 max-w-xs rounded-lg bg-red-500/20 p-3 text-center text-xs text-red-200">
          {err}
        </div>
      )}

      {(phase === 'failed' || allConfirmed) && (
        <div className="mt-4 flex flex-col items-center gap-2">
          {allConfirmed ? (
            <div className="text-sm text-eva-teal">
              {confirmed} photo{confirmed === 1 ? '' : 's'} confirmed
            </div>
          ) : (
            <div className="text-sm text-red-300">
              {failedCount} item{failedCount === 1 ? '' : 's'} failed
            </div>
          )}
          {allConfirmed ? (
            <button
              onClick={() => navigate('/')}
              className="rounded-lg bg-eva-teal px-4 py-2 text-sm font-semibold"
            >
              Back to Home
            </button>
          ) : (
            <button
              onClick={() => {
                setErr(null);
                setPhase('uploading');
                void retryFailedItems(jobId).then(() => drain());
              }}
              className="rounded-lg bg-eva-teal px-4 py-2 text-sm font-semibold"
            >
              Retry
            </button>
          )}
          {!allConfirmed && (
            <button
              onClick={() => navigate(`/job/${jobId}`)}
              className="text-xs text-white/70 underline"
            >
              Back to job
            </button>
          )}
        </div>
      )}
    </div>
  );
}
